// src/contexts/ParentAuthContext.tsx
import React, { createContext, useContext, useEffect, useState } from 'react'
import type { User } from 'firebase/auth'
import { onAuthStateChanged } from 'firebase/auth'
import { doc, setDoc } from 'firebase/firestore'
import { auth, db } from '../lib/firebase-config'

interface ParentAuthContextType {
  user: User | null
  loading: boolean
  error: Error | undefined
}

const ParentAuthContext = createContext<ParentAuthContextType | undefined>(undefined)

export const ParentAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | undefined>(undefined)

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user)
      
      if (user) {
        try {
          const parentRef = doc(db, 'parents', user.uid)
          const { getDoc } = await import('firebase/firestore')
          const docSnap = await getDoc(parentRef)
          
          if (docSnap.exists()) {
            // Only update lastLoginAt for existing users
            await setDoc(parentRef, { lastLoginAt: new Date() }, { merge: true })
          }
          // Profile will be created by login flow if needed
        } catch (err) {
          console.error('Failed to check parent profile:', err)
          setError(err as Error)
        }
      }
      
      setLoading(false)
    })

    return () => unsubscribe()
  }, [])

  return (
    <ParentAuthContext.Provider value={{ user, loading, error }}>
      {children}
    </ParentAuthContext.Provider>
  )
}

export const useParentAuth = () => {
  const context = useContext(ParentAuthContext)
  if (context === undefined) {
    throw new Error('useParentAuth must be used within a ParentAuthProvider')
  }
  return context
}